import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, WA_MESSAGES, WHATSAPP_DISPLAY, wa, track } from "../../data/site";
import { scrollToId } from "../../lib/scroll";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-nav shadow-[0_8px_30px_rgba(11,25,44,0.08)]" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a
          href="#inicio"
          data-testid="navbar-logo"
          aria-label="TopEstofos — início"
          onClick={(e) => goTo(e, "#inicio")}
        >
          <Logo />
        </a>

        <div className="hidden items-center gap-7 lg:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`navbar-link-${link.href.slice(1)}`}
              onClick={(e) => goTo(e, link.href)}
              className="text-sm font-medium text-slate-600 transition-colors duration-300 hover:text-brand"
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="hidden items-center gap-4 lg:flex">
          <a
            href="tel:+351920037468"
            data-testid="navbar-phone-link"
            className="text-sm font-semibold text-navy transition-colors hover:text-brand"
          >
            {WHATSAPP_DISPLAY}
          </a>
          <a
            href={wa(WA_MESSAGES.default)}
            target="_blank"
            rel="noopener noreferrer"
            id="cta-nav"
            data-testid="navbar-cta"
            data-event="whatsapp_click"
            onClick={() => track("whatsapp_click", { location: "navbar" })}
            className="cta-whatsapp cta-orcamento inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-600 hover:shadow-xl hover:shadow-brand/30 active:translate-y-0"
          >
            <MessageCircle className="h-4 w-4" />
            Solicitar orçamento
          </a>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <a
            href={wa(WA_MESSAGES.default)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-whatsapp-mobile"
            data-event="whatsapp_click"
            onClick={() => track("whatsapp_click", { location: "navbar_mobile" })}
            className="cta-whatsapp grid h-10 w-10 place-items-center rounded-full bg-whatsapp text-white shadow-lg shadow-whatsapp/30"
            aria-label="Falar no WhatsApp"
          >
            <MessageCircle className="h-5 w-5" />
          </a>
          <button
            data-testid="navbar-menu-toggle"
            onClick={() => setOpen(!open)}
            className="grid h-10 w-10 place-items-center rounded-full text-navy transition-colors hover:bg-mist"
            aria-label={open ? "Fechar menu" : "Abrir menu"}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="navbar-mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="glass-nav mx-4 mb-4 rounded-2xl border border-slate-100 p-3 shadow-xl shadow-navy/10 lg:hidden"
          >
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`navbar-mobile-link-${link.href.slice(1)}`}
                onClick={(e) => goTo(e, link.href)}
                className="block rounded-xl px-4 py-3.5 text-base font-medium text-navy transition-colors hover:bg-mist hover:text-brand"
              >
                {link.label}
              </a>
            ))}
            <a
              href={wa(WA_MESSAGES.default)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="navbar-mobile-cta"
              data-event="whatsapp_click"
              onClick={() => track("whatsapp_click", { location: "navbar_drawer" })}
              className="cta-whatsapp cta-orcamento mt-2 flex items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3.5 text-base font-semibold text-white"
            >
              <MessageCircle className="h-5 w-5" />
              Solicitar orçamento
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
